/**
 * The details panel's height: how tall it opens, how far it can be dragged,
 * and where the chosen height is kept between visits.
 *
 * Remembered per browser, like the list's refresh rate, because how much
 * room someone wants for the panel is a preference about their screen, not
 * a fact about what they are looking at. The URL carries the latter.
 */

const STORAGE_KEY = 'scheduledJobs.panelHeight'

/** Short enough to keep the header and one line of settings visible. */
export const MIN_PANEL_HEIGHT = 160

/** Leaves enough of the list showing that the selected row can stay in view. */
const MAX_PANEL_FRACTION = 0.85

/** Where the panel opens before anyone has dragged it. */
const DEFAULT_PANEL_FRACTION = 0.3

/** The tallest the panel may be on this viewport. Never below the minimum. */
export function maxPanelHeight(viewportHeight: number): number {
    return Math.max(MIN_PANEL_HEIGHT, Math.floor(viewportHeight * MAX_PANEL_FRACTION))
}

/** Keep a requested height within what the viewport can show. */
export function clampPanelHeight(px: number, viewportHeight: number): number {
    if (!Number.isFinite(px)) return MIN_PANEL_HEIGHT
    return Math.min(maxPanelHeight(viewportHeight), Math.max(MIN_PANEL_HEIGHT, Math.round(px)))
}

/** The height to open at: the remembered one if there is one, else the default. */
export function initialPanelHeight(viewportHeight: number): number {
    const fallback = clampPanelHeight(viewportHeight * DEFAULT_PANEL_FRACTION, viewportHeight)
    try {
        const saved = Number(window.localStorage.getItem(STORAGE_KEY))
        // Anything unrecognised (absent, hand-edited, NaN) falls back.
        return saved > 0 ? clampPanelHeight(saved, viewportHeight) : fallback
    } catch {
        return fallback
    }
}

/** Remember a height. Storage can be unavailable (private windows); that is not an error. */
export function storePanelHeight(px: number): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, String(Math.round(px)))
    } catch {
        // Nothing to do: the height still applies for this visit.
    }
}
